import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { auditRegisterQuery } from '../../api/auditFindings'
import { ApiError } from '../../api/client'
import { Forbidden } from '../../ui/Forbidden'
import { Content } from '../../ui/Shell'
import { Spinner } from '../../ui/Spinner'
import { usePageStyles } from '../../ui/usePageStyles'
import { AdmPageHead } from '../../softsee/adm/AdmPageHead'
import { FindingRow } from './findings'

/**
 * `settings/Audit::register` - registrul de constatari, la
 * /app/settings/audit/register. Macheta: `settings/audit/register.php`.
 *
 * O constatare traieste mai mult decat rularea care a gasit-o. Fotografia
 * raspunde la "ce am gasit atunci"; asta raspunde la "ce mai e deschis", si
 * e un ecran separat fiindca e o intrebare separata - cea pe care o pune un
 * comitet, nu un auditor.
 *
 * Aceeasi problema gasita de trei ori e un singur rand, cu data primei
 * vederi alaturi: "deschisa de patru luni" e cu totul altceva decat "gasita
 * azi". Termenul vine din severitate, fiindca asa defineste manualul o
 * severitate - iar mutarea lui cere un motiv scris (`FindingRow`).
 *
 * Foile sunt ale portalului: `adm.css` (capul de pagina, cardurile) si
 * `audit.css` (pastilele, randurile scadente). Acelasi markup, aceleasi clase.
 */

const STYLES = ['css/adm.css', 'css/audit.css']

export function AuditRegisterPage({ all }: { all: boolean }) {
  const page = useQuery(auditRegisterQuery(all))
  usePageStyles(STYLES, page.data?.version ?? '')

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const { rows, ready, counts, openOnly, states, mayRun, overdue } = page.data

  return (
    <Content>
      <div id="content" className="od-audit od-audit-register">
        <AdmPageHead
          tone="red-intense"
          icon="fa-clipboard-list"
          title="Findings"
          note="What every audit found and nobody has closed yet. The due date comes from the severity, because that is what the manual says a severity means."
          stats={[
            { value: String(rows.length), label: openOnly ? 'open' : 'in all', tone: 'red-intense' },
            { value: String(overdue), label: 'past their date', tone: 'red-intense' },
            { value: counts !== null ? String(counts.serious) : '—', label: 'critical or high', tone: 'red-intense' },
          ]}
          actions={
            <Link className="btn btn-default" to="/settings/audit/register" search={openOnly ? { all: 1 } : {}}>
              {openOnly ? 'Show closed as well' : 'Open only'}
            </Link>
          }
        />

        {!ready ? (
          <div className="adm-card">
            <strong>The findings table is not on this instance yet.</strong>{' '}
            <span className="adm-muted">Change Log &rsaquo; Re-apply schema creates it.</span>
          </div>
        ) : rows.length === 0 ? (
          <div className="adm-empty-inline">
            <i className="fas fa-check"></i>
            {openOnly
              ? 'Nothing open. Either the estate is in good order, or no audit has run yet.'
              : 'No findings recorded yet.'}
          </div>
        ) : (
          <div className="adm-card od-audit-runs">
            <table className="table od-audit-table">
              <thead>
                <tr>
                  <th className="od-audit-th-area">Severity</th>
                  <th>Finding</th>
                  <th>Due</th>
                  <th>Owner</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <FindingRow key={row.fingerprint} row={row} states={states} mayRun={mayRun} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Content>
  )
}
